import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Bell, Clock, Mail, MessageSquare, Calendar, Send, 
  RefreshCw, CheckCircle, AlertCircle, Settings, Plus,
  Edit, Archive, Trash2, RotateCcw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';

const ReminderSettingsTab = () => {
  const [status, setStatus] = useState(null);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualReminder, setManualReminder] = useState({
    recipient_email: '',
    recipient_name: '',
    reminder_type: '24h',
    booking_id: '',
    message: ''
  });
  const [editModal, setEditModal] = useState({ open: false, reminder: null });
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusRes, logsRes] = await Promise.all([
        api.get('/reminders/status'),
        api.get('/reminders/logs').catch(() => ({ data: [] }))
      ]);
      setStatus(statusRes.data);
      setReminderLogs(logsRes.data?.reminders || []);
    } catch (error) {
      toast.error('Failed to load reminder data');
    } finally {
      setLoading(false);
    }
  };

  const sendTestReminders = async () => {
    setSending(true);
    try {
      const response = await api.post('/reminders/send-test');
      toast.success(
        `Sent ${response.data['24_hour_reminders']?.reminders_sent || 0} 24h reminders and ${response.data['same_day_reminders']?.same_day_reminders_sent || 0} same-day reminders`
      );
      fetchData();
    } catch (error) {
      toast.error('Failed to send test reminders');
    } finally {
      setSending(false);
    }
  };

  const handleSendManualReminder = async (e) => {
    e.preventDefault();
    if (!manualReminder.recipient_email.trim()) {
      toast.error('Please enter recipient email');
      return;
    }
    setSending(true);
    try {
      await api.post('/reminders/send-manual', manualReminder);
      toast.success('Manual reminder sent!');
      setManualReminder({ recipient_email: '', recipient_name: '', reminder_type: '24h', booking_id: '', message: '' });
      setShowManualForm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSending(false);
    }
  };

  const handleResendReminder = async (reminder) => {
    if (!window.confirm(`Resend reminder to ${reminder.recipient_email}?`)) return;
    setSending(true);
    try {
      await api.post(`/reminders/${reminder.id}/resend`);
      toast.success('Reminder resent!');
      fetchData();
    } catch (error) {
      toast.error('Failed to resend reminder');
    } finally {
      setSending(false);
    }
  };

  const handleArchiveReminder = async (reminderId) => {
    if (!window.confirm('Archive this reminder?')) return;
    try {
      await api.post(`/reminders/${reminderId}/archive`);
      toast.success('Reminder archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive reminder');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Delete this reminder permanently?')) return;
    try {
      await api.delete(`/reminders/${reminderId}`);
      toast.success('Reminder deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleOpenEditModal = (reminder) => {
    setEditData({
      recipient_email: reminder.recipient_email || '',
      recipient_name: reminder.recipient_name || '',
      reminder_type: reminder.reminder_type || '24h',
      status: reminder.status || 'sent',
      message: reminder.message || ''
    });
    setEditModal({ open: true, reminder });
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/reminders/${editModal.reminder.id}`, editData);
      toast.success('Reminder updated');
      setEditModal({ open: false, reminder: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="reminders-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Booking Reminders</h1>
          <p className="text-[var(--text-muted)]">Automated reminders for customers and workers</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowManualForm(true)} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Send Manual
          </Button>
          <Button onClick={sendTestReminders} disabled={sending}>
            <Send className="w-4 h-4 mr-2" />
            {sending ? 'Sending...' : 'Send Test Reminders'}
          </Button>
        </div>
      </div>

      {/* Manual Reminder Modal */}
      {showManualForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowManualForm(false)}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Send className="h-5 w-5 text-[#D4AF37]" />
              Send Manual Reminder
            </h2>
            <form onSubmit={handleSendManualReminder} className="space-y-4">
              <div>
                <Label>Recipient Email *</Label>
                <Input
                  type="email"
                  value={manualReminder.recipient_email}
                  onChange={(e) => setManualReminder({ ...manualReminder, recipient_email: e.target.value })}
                  placeholder="customer@email.com"
                  required
                />
              </div>
              <div>
                <Label>Recipient Name</Label>
                <Input
                  value={manualReminder.recipient_name}
                  onChange={(e) => setManualReminder({ ...manualReminder, recipient_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Reminder Type</Label>
                <Select value={manualReminder.reminder_type} onValueChange={(v) => setManualReminder({ ...manualReminder, reminder_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24h">24-Hour Reminder</SelectItem>
                    <SelectItem value="same_day">Same-Day Reminder</SelectItem>
                    <SelectItem value="custom">Custom Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Booking ID (Optional)</Label>
                <Input
                  value={manualReminder.booking_id}
                  onChange={(e) => setManualReminder({ ...manualReminder, booking_id: e.target.value })}
                  placeholder="Enter booking ID if applicable"
                />
              </div>
              <div>
                <Label>Custom Message (Optional)</Label>
                <Textarea
                  value={manualReminder.message}
                  onChange={(e) => setManualReminder({ ...manualReminder, message: e.target.value })}
                  placeholder="Leave blank for default reminder message..."
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={sending} className="flex-1 btn-primary">
                  {sending ? 'Sending...' : 'Send Reminder'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowManualForm(false)}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Reminder Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditModal({ open: false, reminder: null })}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Edit className="h-5 w-5 text-[#D4AF37]" />
              Edit Reminder Log
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Recipient Email</Label>
                <Input
                  value={editData.recipient_email}
                  onChange={(e) => setEditData({ ...editData, recipient_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Recipient Name</Label>
                <Input
                  value={editData.recipient_name}
                  onChange={(e) => setEditData({ ...editData, recipient_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveEdit} className="flex-1 btn-primary">Save Changes</Button>
                <Button variant="outline" onClick={() => setEditModal({ open: false, reminder: null })}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <span className="text-sm text-[var(--text-muted)]">24h Reminders Pending</span>
          </div>
          <p className="text-3xl font-bold">{status?.pending_24h_reminders || 0}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">For tomorrow's bookings</p>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <Calendar className="w-5 h-5 text-orange-600" />
            </div>
            <span className="text-sm text-[var(--text-muted)]">Same-Day Pending</span>
          </div>
          <p className="text-3xl font-bold">{status?.pending_same_day_reminders || 0}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">For today's bookings</p>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[var(--success)]/15 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-[var(--success)]" />
            </div>
            <span className="text-sm text-[var(--text-muted)]">Sent Today</span>
          </div>
          <p className="text-3xl font-bold">{status?.reminders_sent_today || 0}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Reminders sent</p>
        </div>
      </div>

      {/* Reminder Logs Table */}
      <div className="card p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2">
            <Bell className="w-5 h-5" /> Recent Reminder Logs
          </h3>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
        
        {reminderLogs.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-muted)]">
            <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No reminder logs yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reminderLogs.map((reminder) => (
              <div key={reminder.id} className="flex items-center justify-between p-4 bg-[var(--background-subtle)] rounded-lg border">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    reminder.status === 'sent' ? 'bg-[var(--success)]/15' : reminder.status === 'archived' ? 'bg-[var(--background-subtle)]' : 'bg-[var(--error)]/15'
                  }`}>
                    {reminder.status === 'sent' ? (
                      <CheckCircle className="w-5 h-5 text-[var(--success)]" />
                    ) : reminder.status === 'archived' ? (
                      <Archive className="w-5 h-5 text-[var(--text-secondary)]" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-[var(--error)]" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{reminder.recipient_email}</p>
                    <p className="text-sm text-[var(--text-muted)]">
                      {reminder.reminder_type === '24h' ? '24-Hour' : reminder.reminder_type === 'same_day' ? 'Same-Day' : reminder.reminder_type} • {formatDate(reminder.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleResendReminder(reminder)} className="text-[#D4AF37] hover:bg-[#D4AF37]/10" title="Resend">
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(reminder)} className="text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]" title="Edit">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleArchiveReminder(reminder.id)} className="text-amber-600 hover:bg-[var(--warning)]/10" title="Archive">
                    <Archive className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteReminder(reminder.id)} className="text-[var(--error)] hover:bg-[var(--error)]/10" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Schedule Info */}
      <div className="card p-6 mb-8">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" /> Reminder Schedule
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex items-start gap-4 p-4 bg-[#D4AF37]/10 rounded-lg">
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Bell className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div>
              <h4 className="font-medium">24-Hour Reminder</h4>
              <p className="text-sm text-[var(--text-muted)]">Daily at 10:00 AM</p>
              <p className="text-xs text-[#D4AF37] mt-1">Email + SMS to customers and workers</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-orange-50 rounded-lg">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h4 className="font-medium">Same-Day Reminder</h4>
              <p className="text-sm text-[var(--text-muted)]">Daily at 7:00 AM</p>
              <p className="text-xs text-orange-600 mt-1">In-app notification for today's bookings</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReminderSettingsTab;
